// The place-page shell, rendered as strings.
//
// Every page about a place — a city's guide, its briefing, a country, a ranking
// — is the same four things in the same order: a map header with the page's
// subject set over it, an answer strip (the one-paragraph answer beside a live
// verdict), a sticky bar (section links and the state sentence), and sections
// built from a small set of shared modules. These are those pieces.
//
// Pure, like city-view.ts: strings in, strings out, no DOM and no app state. The
// pre-renders (build-pages, build-resources) and both clients (the app's city
// panel, the documents' place.js) call the same functions, which is the only
// reason a guide and a briefing cannot drift apart again. The classes are all
// `pl-…` and are styled once, in public/base.css.

import { countrySlug } from './slug.ts';

export function esc(s: unknown): string {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// ── the top row ──────────────────────────────────────────────────────────────
export interface Crumb {
  name: string;
  href?: string;
  /** Extra attributes for a crumb the client handles itself (the app's "Map"). */
  attrs?: string;
}

// "› Guides › Australia". The separators are the design's, in --fc-sep; each
// crumb is a dashed word, like every other thing on the page that goes somewhere.
export function crumbsHtml(crumbs: readonly Crumb[]): string {
  if (!crumbs.length) return '';
  const items = crumbs
    .map((c) => {
      const a = c.href != null
        ? `<a class="pl-crumb" href="${esc(c.href)}"${c.attrs ? ` ${c.attrs}` : ''}>${esc(c.name)}</a>`
        : `<button type="button" class="pl-crumb"${c.attrs ? ` ${c.attrs}` : ''}>${esc(c.name)}</button>`;
      return `<span class="pl-crumb-item"><span class="pl-sep" aria-hidden="true">›</span>${a}</span>`;
    })
    .join('');
  return `<nav class="pl-crumbs" aria-label="Breadcrumb">${items}</nav>`;
}

// The app's own two: its "Map" crumb and its "Back to the map" are buttons the
// app handles (data-close-city → the full map), not links that would reload it.
export const BACK_TO_MAP = '<button type="button" class="pl-linkbtn" data-close-city>Back to the map</button>';
export function briefingCrumbs(country: string | undefined, countryHref?: string): Crumb[] {
  const out: Crumb[] = [{ name: 'Map', attrs: 'data-close-city' }];
  if (country) out.push({ name: country, href: countryHref ?? `/${countrySlug(country)}/` });
  return out;
}

export const BRAND_HTML =
  '<a class="pl-brand" href="/" aria-label="flycatcherclub — home"><span class="pl-dot" aria-hidden="true"></span><span>flycatcherclub<span class="pl-brand-tld">.com</span></span></a>';

// A document's own top row. The app does not use this: its wordmark and its
// corner words are already on screen, and the briefing only adds the crumbs.
// The three words lead into the app, which is where those three jobs live.
export function topRowHtml(crumbs: readonly Crumb[], compareHref = '/#compare'): string {
  return `<header class="pl-top">${BRAND_HTML}${crumbsHtml(crumbs)}`
    + `<div class="pl-words">`
    + `<a class="pl-word pl-desk" href="/">Find your day</a>`
    + `<a class="pl-word pl-desk" href="${esc(compareHref)}">Compare places</a>`
    + `<a class="pl-word pl-desk" href="/#search">Search a city</a>`
    + `<button id="theme-toggle" class="pl-word pl-word-quiet pl-theme" type="button" aria-label="Switch colour theme"></button>`
    + `</div></header>`;
}

// ── the masthead ─────────────────────────────────────────────────────────────
export interface MastOpts {
  /** The page TYPE ("Best time to visit"), never the place: the place is the title. */
  eyebrow: string;
  title: string;
  sub: string;
  /** Guide | Briefing — a city's two views. Each is a link: they are two URLs. */
  tabs?: { guideHref: string; briefingHref: string; active: 'guide' | 'briefing' } | null;
  mapButton?: boolean;
}

export function mastHtml(o: MastOpts): string {
  const tab = (key: 'guide' | 'briefing', label: string, href: string) =>
    `<a class="pl-seg-btn${o.tabs!.active === key ? ' on' : ''}" href="${esc(href)}" data-pl-view="${key}"${o.tabs!.active === key ? ' aria-current="page"' : ''}>${label}</a>`;
  const tabs = o.tabs
    ? `<div class="pl-seg pl-seg-glass" aria-label="View">${tab('guide', 'Guide', o.tabs.guideHref)}${tab('briefing', 'Briefing', o.tabs.briefingHref)}</div>`
    : '';
  const map = o.mapButton === false ? '' : '<button type="button" class="pl-mapbtn" data-pl-map><span aria-hidden="true">⤢</span> Map view</button>';
  // A place's name is short and takes the display size; a ranking's title is a
  // phrase and would run to four lines at it.
  const long = o.title.length > 18;
  return `<div class="pl-mast-text">`
    + `<div class="pl-eyebrow">${esc(o.eyebrow)}</div>`
    + `<h1 class="pl-title${long ? ' pl-title-long' : ''}">${esc(o.title)}</h1>`
    + `<div class="pl-sub">${esc(o.sub)}</div>`
    + `</div>${tabs}${map}`;
}

// ── the answer strip ─────────────────────────────────────────────────────────
export interface Fact {
  label: string;
  value: string;
  sub?: string;
  /** The dekad this fact is AT; its sub line then goes there on click. */
  gotoI?: number | null;
}

export function factsHtml(facts: readonly Fact[]): string {
  if (!facts.length) return '';
  const items = facts
    .map((f) => {
      const sub = f.sub == null
        ? ''
        : f.gotoI == null
          ? `<div class="pl-fact-sub">${esc(f.sub)}</div>`
          : `<button type="button" class="pl-fact-sub pl-goto" data-goto-i="${f.gotoI}">${esc(f.sub)}</button>`;
      return `<div class="pl-fact"><div class="pl-fact-label">${esc(f.label)}</div><div class="pl-fact-value">${esc(f.value)}</div>${sub}</div>`;
    })
    .join('');
  return `<div class="pl-facts">${items}</div>`;
}

export interface VerdictRow {
  label: string;
  value: string;
  /** good | mid | bad | na — colours the value as a judgement. */
  cls?: string;
}

export interface Verdict {
  /** The big figure: a score, a percentage, a temperature. */
  big: string;
  /** The word beside it ("Good"). */
  band?: string;
  /** The figure's colour — the data ramp's, so the number and the map agree. */
  color?: string;
  sub: string;
  rows: VerdictRow[];
}

// The verdict column. `null` is a real state — a measurement showing and
// nothing rated — and it renders as nothing rather than as an invented score.
export function verdictHtml(v: Verdict | null): string {
  if (!v) return '';
  const rows = v.rows
    .map((r) => (r.cls === 'pad'
      ? '<div class="pl-vrow pl-vrow-pad" aria-hidden="true"><span class="pl-vrow-label">&nbsp;</span></div>'
      : `<div class="pl-vrow${r.cls ? ` drv-${r.cls}` : ''}"><span class="pl-vrow-label">${esc(r.label)}</span><span class="pl-vrow-value">${esc(r.value)}</span></div>`))
    .join('');
  return `<div class="pl-score"${v.color ? ` style="color:${v.color}"` : ''}>${esc(v.big)}${v.band ? `<span class="pl-band-word">${esc(v.band)}</span>` : ''}</div>`
    + `<div class="pl-verdict-sub">${esc(v.sub)}</div>`
    + (rows ? `<div class="pl-vrows">${rows}</div>` : '');
}

// `left` is HTML the caller built (a standfirst with its links, or the records'
// eyebrow); the verdict goes in its own element so a client can repaint it on
// every hover without touching the paragraph beside it.
export function answerHtml(left: string, verdict: Verdict | null): string {
  return `<div class="pl-answer"><div class="pl-answer-left">${left}</div><div class="pl-verdict" id="pl-verdict">${verdictHtml(verdict)}</div></div>`;
}

// ── the sticky bar ───────────────────────────────────────────────────────────
export interface BarOpts {
  links: readonly { id: string; label: string }[];
  /** "Showing Easy outdoors · mid April · midday". Omitted on a page with no state. */
  state?: { rating: string; when: string; bucket: string; lead?: string } | null;
  mapButton?: boolean;
}

export function barHtml(o: BarOpts): string {
  const links = o.links.map((l) => `<a class="pl-bar-link pl-desk" href="#${esc(l.id)}">${esc(l.label)}</a>`).join('');
  const st = o.state;
  const state = st
    ? `<div class="pl-state">`
      + `<span class="pl-state-lead pl-desk">${esc(st.lead ?? 'Showing')}</span> `
      + `<button type="button" class="pl-state-word" id="pl-state-rating" data-pl-rating-menu aria-haspopup="true" aria-expanded="false">${esc(st.rating)}</button>`
      + `<span class="pl-sep" aria-hidden="true">·</span>`
      + `<button type="button" class="pl-state-dash" id="pl-state-when" data-pl-when>${esc(st.when)}</button>`
      + `<span class="pl-sep" aria-hidden="true">·</span>`
      + `<button type="button" class="pl-state-dash" id="pl-state-bucket" data-pl-bucket-menu>${esc(st.bucket)}</button>`
      + `</div>`
    : '<div class="pl-state"></div>';
  const map = o.mapButton === false ? '' : '<button type="button" class="pl-mapbtn pl-mapbtn-sm" data-pl-map><span aria-hidden="true">⤢</span> Map</button>';
  // The menu's anchor is a zero-height sibling INSIDE the bar's sticky box, so
  // the panel rides with the bar however far the page has scrolled.
  return `<nav class="pl-bar" id="pl-bar" aria-label="On this page"><span class="pl-dot" aria-hidden="true"></span>${links}${state}${map}`
    + `<div id="pl-menu" class="pl-menu" role="menu" hidden></div></nav>`;
}

// ── sections ─────────────────────────────────────────────────────────────────
// A segmented toggle. `attr` is the data attribute each button carries and the
// one delegated handler reads ("chart-var" → data-chart-var="t").
export function segHtml(attr: string, items: readonly { key: string; label: string }[], active: string, cls = ''): string {
  return `<div class="pl-seg${cls ? ` ${cls}` : ''}" role="group">`
    + items.map((it) => `<button type="button" class="pl-seg-btn${it.key === active ? ' on' : ''}" data-${attr}="${esc(it.key)}" aria-pressed="${it.key === active}">${esc(it.label)}</button>`).join('')
    + `</div>`;
}

export interface SectionOpts {
  /** The muted line on the heading's own baseline. Part of the <h2>, so the
   *  words a search is made of ("Sydney climate by month") stay in the heading. */
  unit?: string;
  /** Controls beside the heading (segmented toggles). */
  tools?: string;
  /** Extra class on the <section>. */
  cls?: string;
}

export function sectionHtml(id: string, title: string, body: string, o: SectionOpts = {}): string {
  const unit = o.unit ? ` <span class="pl-unit">${esc(o.unit)}</span>` : '';
  return `<section class="pl-sec${o.cls ? ` ${o.cls}` : ''}" id="${esc(id)}">`
    + `<div class="pl-sec-head"><h2 class="pl-h2">${esc(title)}${unit}</h2>${o.tools || ''}</div>`
    + body
    + `</section>`;
}

// A card that states a place and a score: "where to go instead", a ranking's
// places with a guide of their own.
export interface PlaceCard {
  name: string;
  where: string;
  href?: string;
  big: string;
  band?: string;
  color?: string;
  /** A second link under the score ("See mid July"). */
  more?: { href: string; text: string };
}

export function placeCardsHtml(cards: readonly PlaceCard[]): string {
  const items = cards
    .map((c) => {
      const name = c.href ? `<a class="pl-card-name" href="${esc(c.href)}">${esc(c.name)}</a>` : `<span class="pl-card-name">${esc(c.name)}</span>`;
      return `<div class="pl-card">${name}<div class="pl-card-where">${esc(c.where)}</div>`
        + `<div class="pl-card-big"${c.color ? ` style="color:${c.color}"` : ''}>${esc(c.big)}${c.band ? `<span class="pl-card-band">${esc(c.band)}</span>` : ''}</div>`
        + (c.more ? `<a class="pl-card-more pl-goto" href="${esc(c.more.href)}">${esc(c.more.text)}</a>` : '')
        + `</div>`;
    })
    .join('');
  return `<div class="pl-cards">${items}</div>`;
}

export interface LinkGroup {
  title: string;
  /** A first link set apart, with an arrow ("Best time to visit Australia →"). */
  lead?: { href: string; text: string };
  links: readonly { href: string; text: string }[];
}

export function linkGroupsHtml(groups: readonly LinkGroup[]): string {
  const items = groups
    .filter((g) => g.lead || g.links.length)
    .map((g) => {
      const lead = g.lead ? `<a class="pl-lgroup-lead" href="${esc(g.lead.href)}">${esc(g.lead.text)} →</a>` : '';
      const links = g.links.length
        ? `<div class="pl-lgroup-links">${g.links.map((l) => `<a href="${esc(l.href)}">${esc(l.text)}</a>`).join('<span class="pl-sep" aria-hidden="true"> · </span>')}</div>`
        : '';
      return `<div class="pl-lgroup"><div class="pl-lgroup-title">${esc(g.title)}</div>${lead}${links}</div>`;
    })
    .join('');
  return items ? `<div class="pl-lgroups">${items}</div>` : '';
}

// ── the footer ───────────────────────────────────────────────────────────────
export interface FooterOpts {
  /** "a decade" — the record's length. */
  span: string;
  credits?: readonly string[];
  /** The app is already the map, so there "Back to the map" is a button. */
  mapLink?: string;
}

// Who the numbers and the ground are owed to. ODbL obliges the OpenStreetMap
// credit wherever the self-hosted basemap may be drawn; the rest is courtesy.
export const DATA_CREDIT = 'Weather: NOAA NCEI ISD Global-Hourly. Basemap: Natural Earth, © OpenStreetMap contributors (ODbL). Cities: SimpleMaps (CC BY 4.0).';

export function footerHtml(o: FooterOpts): string {
  const credits = [...(o.credits || []), DATA_CREDIT].filter(Boolean).map((c) => `<p class="pl-credit">${esc(c)}</p>`).join('');
  return `<footer class="pl-foot">`
    + `<p>Every figure is ${esc(o.span || 'years')} of weather-station observations blended into one typical year. It describes what a time of year is usually like, not a forecast.</p>`
    + credits
    + `<p><a href="/resources/">All guides</a> · ${o.mapLink ?? '<a href="/">Back to the map</a>'} · <a href="/privacy">Privacy</a></p>`
    + `</footer>`;
}

// ── a map pin, as data ───────────────────────────────────────────────────────
// What a document tells its client to put on the map. Embedded in the page as
// JSON (see resources/render.ts), so the pins need no request of their own.
export interface Pin {
  lat: number;
  lon: number;
  name: string;
  /** Where clicking it goes. */
  href?: string;
  /** The page's subject: the larger dot. */
  subject?: boolean;
  /** A ranking's number. */
  n?: number;
  /** A score, for the pin's colour and (on the subject) the figure beside it. */
  score?: number | null;
  /** The muted words after the name ("84 mid Jul"). */
  note?: string;
  /** Country pages: one character per dekad and use, '1' where in season. */
  on?: Record<string, string>;
}
